import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { useForm } from "@tanstack/react-form";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import Form from "src/components/base/form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    GlobeAltIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import { Text } from "src/components/base/text";
import { ExtraDnsRecordType, FullExtraDnsDomain, FullExtraDnsRecord } from "src/api/generated/frontend_internal_v1";
import DeleteExtraDomainDialog from "src/components/dialogs/delete-extra-domain";
import UpdateExtraDomainDialog from "src/components/dialogs/update-extra-domain";
import { Subheading } from "src/components/base/heading";
import { EmptyState } from "src/components/base/empty-state";
import { DeviceLinkBadge } from "src/components/badges/device-link";
import Address from "ipaddr.js";

/**
 * Extra DNS records overview. The data model collapses to (ipv4?, ipv6?) per domain because
 * Tailscale clients only resolve one record per type per domain. Domains that share the
 * same IP pair are visually grouped; all create / edit / delete flows live in the
 * AddExtraDomainDialog and UpdateExtraDomainDialog respectively (no per-record dialogs).
 *
 * @returns the rendered route component
 */
export default function ExtraRecordsDnsOverview() {
    const [t] = useTranslation("admin-dns-extra-records");
    const [openDeleteExtraDomain, setDeleteExtraDomain] = useState<FullExtraDnsDomain>();
    const [openUpdateExtraDomain, setUpdateExtraDomain] = useState<FullExtraDnsDomain>();

    const navigate = useNavigate();
    const router = useRouter();

    const { search } = Route.useSearch();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: { search },
    });

    const groups = useMemo(() => groupByIpPair(data.list), [data.list]);

    return (
        <div className={"flex flex-col gap-5"}>
            <div className={"flex flex-col gap-1"}>
                <Subheading>{t("heading.extra-records")}</Subheading>
                <Text>{t("description.extra-records")}</Text>
            </div>

            <div className={"flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"}>
                <Form className={"w-full max-w-md"} onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field
                                name={"search"}
                                validators={{
                                    onChangeAsync: async ({ fieldApi }) => {
                                        await navigate({
                                            to: "/a/dns/extra-records",
                                            search: { search: fieldApi.state.value },
                                        });
                                    },
                                    onChangeAsyncDebounceMs: 500,
                                }}
                            >
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.search")}</Label>
                                        <Input
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </Form>
                <PrimaryButton href={"/a/dns/extra-records/create-new-domain"}>
                    <PlusIcon />
                    {t("button.add-extra-domain")}
                </PrimaryButton>
            </div>

            {groups.length === 0 ? (
                <EmptyState
                    icon={<GlobeAltIcon />}
                    title={t("empty.title")}
                    description={t("empty.description")}
                    action={
                        <Button href={"/a/dns/extra-records/create-new-domain"} color={"blue"}>
                            <PlusIcon />
                            {t("button.add-extra-domain")}
                        </Button>
                    }
                />
            ) : (
                <div
                    className={
                        "divide-y divide-zinc-950/5 overflow-hidden rounded-(--radius-card) bg-(--surface-card) shadow-(--shadow-card-sm) ring-1 ring-zinc-950/5 dark:divide-white/5 dark:ring-white/10"
                    }
                >
                    {groups.map((group) => (
                        <GroupSection
                            key={group.key}
                            group={group}
                            t={t}
                            onEditDomain={(domain) => setUpdateExtraDomain(domain)}
                            onDeleteDomain={(domain) => setDeleteExtraDomain(domain)}
                        />
                    ))}
                </div>
            )}

            <Suspense>
                <UpdateExtraDomainDialog
                    dns={openUpdateExtraDomain}
                    isOpen={!!openUpdateExtraDomain}
                    onClose={() => setUpdateExtraDomain(undefined)}
                    onSubmit={async () => {
                        setUpdateExtraDomain(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
                <DeleteExtraDomainDialog
                    dns={openDeleteExtraDomain?.uuid}
                    isOpen={!!openDeleteExtraDomain}
                    onClose={() => setDeleteExtraDomain(undefined)}
                    onDelete={async () => {
                        setDeleteExtraDomain(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
            </Suspense>
        </div>
    );
}

/**
 * One row in the list view: a set of domains that share the same (ipv4, ipv6) record pair,
 * rendered together to deduplicate visual noise.
 */
type DomainGroup = {
    /** Stable React key — derived from the shared IP pair, or the domain UUID for empty groups. */
    key: string;
    /** Domains in the group, sorted alphabetically by `from_dns`. */
    domains: FullExtraDnsDomain[];
    /** First A record found in the group (all domains in the group share the same IPv4). */
    ipv4: FullExtraDnsRecord | undefined;
    /** First AAAA record found in the group (all domains share the same IPv6). */
    ipv6: FullExtraDnsRecord | undefined;
};

const findA = (d: FullExtraDnsDomain) => d.records.find((r) => r.record_type === ExtraDnsRecordType.A);
const findAaaa = (d: FullExtraDnsDomain) => d.records.find((r) => r.record_type === ExtraDnsRecordType.AAAA);

/**
 * Group domains that share the same (ipv4, ipv6) pair. Domains without records sit in their
 * own group keyed by uuid (so they don't all collapse together).
 *
 * @param list flat list of domains from the API
 * @returns groups sorted alphabetically by the first domain in each
 */
function groupByIpPair(list: FullExtraDnsDomain[]): DomainGroup[] {
    const map = new Map<string, DomainGroup>();
    for (const domain of list) {
        const ipv4 = findA(domain);
        const ipv6 = findAaaa(domain);
        const key = ipv4 || ipv6 ? `${ipv4?.to_ip ?? ""}|${ipv6?.to_ip ?? ""}` : `__empty__::${domain.uuid}`;
        let group = map.get(key);
        if (!group) {
            group = { key, domains: [], ipv4, ipv6 };
            map.set(key, group);
        }
        group.domains.push(domain);
    }
    const groups = [...map.values()];
    for (const g of groups) {
        g.domains.sort((a, b) => a.from_dns.localeCompare(b.from_dns, undefined, { sensitivity: "base" }));
    }

    groups.sort((a, b) => {
        // eslint-disable-next-line jsdoc/require-jsdoc
        function compareBytes(a: number[], b: number[]) {
            for (let i = 0; i < a.length; i++) {
                if (a[i] > b[i]) {
                    return 1;
                } else if (a[i] < b[i]) {
                    return -1;
                }
            }
            return 0;
        }

        // eslint-disable-next-line jsdoc/require-jsdoc
        function compareIp(a: string | undefined, b: string | undefined) {
            if (a === undefined && b === undefined) return 0;
            if (a === undefined) return 1;
            if (b === undefined) return -1;
            return compareBytes(Address.parse(a).toByteArray(), Address.parse(b).toByteArray());
        }

        const v4Cmp = compareIp(a.ipv4?.to_ip, b.ipv4?.to_ip);
        if (v4Cmp !== 0) return v4Cmp;
        return compareIp(a.ipv6?.to_ip, b.ipv6?.to_ip);
    });
    return groups;
}

/**
 * Properties for {@link GroupSection}.
 */
type GroupSectionProps = {
    /** The group to render. */
    group: DomainGroup;
    /** i18n function from the parent (already namespaced to `admin-dns-extra-records`). */
    t: (key: string, opts?: Record<string, unknown>) => string;
    /** Triggered when the user picks "Edit" on a domain. */
    onEditDomain: (domain: FullExtraDnsDomain) => void;
    /** Triggered when the user picks "Delete" on a domain. */
    onDeleteDomain: (domain: FullExtraDnsDomain) => void;
};

/**
 * Renders one group section: stacked domain headers with kebab menus, an optional
 * "shared by" hint, and the (ipv4, ipv6) record rows beneath.
 *
 * @param props see {@link GroupSectionProps}
 * @returns the rendered section
 */
function GroupSection(props: GroupSectionProps) {
    const { group, t, onEditDomain, onDeleteDomain } = props;
    const collapseAt = 3;
    const collapsible = group.domains.length > collapseAt;
    const [expanded, setExpanded] = useState(false);
    const visibleDomains = collapsible && !expanded ? group.domains.slice(0, collapseAt) : group.domains;
    const hiddenCount = group.domains.length - visibleDomains.length;
    const isShared = group.domains.length > 1;

    return (
        <section className={"px-4 py-3"}>
            <ul className={"flex flex-col"}>
                {visibleDomains.map((domain) => (
                    <li key={domain.uuid} className={"group/header flex items-center justify-between gap-3 py-0.5"}>
                        <div className={"flex min-w-0 flex-col"}>
                            <span className={"truncate text-sm/5 font-semibold text-zinc-950 dark:text-white"}>
                                {domain.from_dns}
                            </span>
                            {domain.description && (
                                <span className={"truncate text-xs/4 text-zinc-500 dark:text-zinc-400"}>
                                    {domain.description}
                                </span>
                            )}
                        </div>
                        <div>
                            <Button
                                onClick={() => onEditDomain(domain)}
                                plain={true}
                                className={
                                    "size-7 shrink-0 opacity-50 transition-opacity group-hover/header:opacity-100 hover:opacity-100 data-active:opacity-100 data-open:opacity-100"
                                }
                            >
                                <PencilSquareIcon />
                            </Button>
                            <Button
                                onClick={() => onDeleteDomain(domain)}
                                plain={true}
                                className={
                                    "size-7 shrink-0 opacity-50 transition-opacity group-hover/header:opacity-100 hover:opacity-100 data-active:opacity-100 data-open:opacity-100"
                                }
                            >
                                <TrashIcon />
                            </Button>
                        </div>
                    </li>
                ))}
            </ul>

            {collapsible && (
                <button
                    type={"button"}
                    onClick={() => setExpanded(!expanded)}
                    className={
                        "mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-zinc-500 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400"
                    }
                >
                    {expanded ? (
                        <>
                            <ChevronUpIcon className={"size-3"} />
                            {t("button.show-fewer-domains")}
                        </>
                    ) : (
                        <>
                            <ChevronDownIcon className={"size-3"} />
                            {t("button.show-more-domains", { count: hiddenCount })}
                        </>
                    )}
                </button>
            )}

            {isShared && !collapsible && (
                <div className={"mt-1 text-[11px] text-zinc-500 dark:text-zinc-400"}>
                    {t("description.shared-by", { count: group.domains.length })}
                </div>
            )}

            <div className={"flex items-center justify-between"}>
                <ul className={"mt-1.5"}>
                    {group.ipv4 && (
                        <RecordRow label={t("label.ipv4")} ip={group.ipv4.to_ip} deviceUuid={group.ipv4.device_uuid} />
                    )}
                    {group.ipv6 && (
                        <RecordRow label={t("label.ipv6")} ip={group.ipv6.to_ip} deviceUuid={group.ipv6.device_uuid} />
                    )}
                </ul>

                <Button
                    outline={true}
                    className={"size-8"}
                    href={"/a/dns/extra-records/create-new-domain"}
                    search={{ ipv4Prefill: props.group.ipv4?.to_ip, ipv6Prefill: props.group.ipv6?.to_ip }}
                >
                    <PlusIcon />
                </Button>
            </div>
        </section>
    );
}

/**
 * Properties for {@link RecordRow}.
 */
type RecordRowProps = {
    /** Short label before the IP — "IPv4" or "IPv6". */
    label: string;
    /** The IP address itself. */
    ip: string;
    /** UUID of the linked device, if any (renders a chip). */
    deviceUuid: string | null | undefined;
};

/**
 * One inline row inside a {@link GroupSection}, rendering a label + IP + optional device chip.
 *
 * @param props see {@link RecordRowProps}
 * @returns the rendered list item
 */
function RecordRow(props: RecordRowProps) {
    const { label, ip, deviceUuid } = props;
    return (
        <li className={"-mx-2 grid grid-cols-[3rem_minmax(0,1fr)] items-center gap-3 rounded-md px-2 py-1"}>
            <span className={"text-[11px] font-semibold tracking-wide text-blue-600 uppercase dark:text-blue-400"}>
                {label}
            </span>
            <div className={"flex min-w-0 items-center gap-3"}>
                <span className={"shrink-0 font-mono text-xs/5 text-zinc-700 dark:text-zinc-300"}>{ip}</span>
                {deviceUuid && <DeviceLink uuid={deviceUuid} />}
            </div>
        </li>
    );
}

/**
 * Properties for {@link DeviceLink}.
 */
type DeviceLinkProps = {
    /** UUID of the device to resolve and link to. */
    uuid: string;
};

/**
 * Resolves the linked device's name lazily and renders a {@link DeviceLinkBadge} for it.
 *
 * @param props see {@link DeviceLinkProps}
 * @returns the rendered badge
 */
function DeviceLink(props: DeviceLinkProps) {
    const { uuid } = props;
    const [name, setName] = useState<string>("…");

    useEffect(() => {
        ApiInternal.devices.getSingle(uuid).then((d) => setName(d.dnsname));
    }, [uuid]);

    return <DeviceLinkBadge device={{ uuid, name }} />;
}

/**
 * URL search params for the extra records list view.
 */
type ExtraRecordsSearch = {
    /** Free-text filter over `from_dns`. Empty string means no filter. */
    search: string;
};
export const Route = createFileRoute("/_admin/a/dns/_dns/extra-records/")({
    component: ExtraRecordsDnsOverview,
    validateSearch: (search: Record<string, unknown>): ExtraRecordsSearch => {
        return { search: (search.search as string) || "" };
    },
    loaderDeps: ({ search: { search } }) => ({ search }),
    loader: async ({ deps }) =>
        await ApiInternal.dns.getAllExtraDns({
            filter_dns: deps.search,
        }),
});
